// @givmo/fundraisers-module — GoalThermometer (CPB-WEB1). Pure presentational fundraising progress bar.
//
// ★ NUMBERS-ARE-THE-LAW: this renders the SERVER's figures verbatim. The fill width is the server
// `goal_progress_percent` (a plain 0–100 number — a bounded ratio, clamped to [0,100] for CSS only);
// the raised/goal labels are the server's `stats.total_raised` / `goal_amount` decimal money STRINGS,
// formatted for display (never parsed to a float). It NEVER computes `raised / goal` — a fundraiser whose
// server percent disagrees with raised÷goal (rounding, caps) still shows the server value.
// Reused by the fundraisers list (compact) and the editor rail (full). Stateless: no queries, no effects.

import { testIdProp, type TestIdProps } from '@givmo/design-system';
import {
  formatFundraiserMoney,
  formatGoalPercent,
  goalPercentBarWidth,
} from './fundraisers-decode';

export interface GoalThermometerProps extends TestIdProps {
  /** Server `stats.total_raised` (dollars, a decimal money STRING) — formatted verbatim, never parsed. */
  raised: string | null | undefined;
  /** Server `goal_amount` (dollars, a decimal money STRING) — null when the fundraiser has no goal. */
  goal: string | null | undefined;
  /** Server `goal_progress_percent` (0–100, a plain number) — the fill width. Null when there is no goal. */
  percent: number | null | undefined;
  /** Compact single-line variant for table rows (default false = the full editor-rail variant). */
  compact?: boolean;
  className?: string;
}

export function GoalThermometer({
  raised,
  goal,
  percent,
  compact = false,
  className,
  testId,
}: GoalThermometerProps) {
  const barWidth = goalPercentBarWidth(percent);
  const percentLabel = formatGoalPercent(percent);
  const hasGoal = barWidth !== null;

  if (compact) {
    return (
      <div className={className} {...testIdProp(testId)}>
        <div className="flex items-baseline justify-between gap-8">
          <span className="text-s13 font-w600 text-text-strong tabular-nums">
            {formatFundraiserMoney(raised)}
          </span>
          {hasGoal ? (
            <span className="text-s12 text-text-tertiary tabular-nums">
              of {formatFundraiserMoney(goal)}
            </span>
          ) : (
            <span className="text-s12 text-text-tertiary">No goal</span>
          )}
        </div>
        {hasGoal && (
          <div
            className="mt-4 h-6 w-full overflow-hidden rounded-full bg-neutral-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={barWidth ?? undefined}
          >
            <div
              className="h-full rounded-full bg-brand-500 transition-[width]"
              style={{ width: `${barWidth}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className} {...testIdProp(testId)}>
      <div className="flex items-baseline justify-between gap-8">
        <span className="text-s20 font-w700 text-text-strong tabular-nums">
          {formatFundraiserMoney(raised)}
        </span>
        {percentLabel && (
          <span className="text-s14 font-w600 text-brand-700 tabular-nums">{percentLabel}</span>
        )}
      </div>
      <p className="mt-2 text-s13 text-text-tertiary">
        {hasGoal ? <>raised of {formatFundraiserMoney(goal)} goal</> : <>raised · no goal set</>}
      </p>
      {hasGoal && (
        <div
          className="mt-10 h-10 w-full overflow-hidden rounded-full bg-neutral-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={barWidth ?? undefined}
        >
          <div
            className="h-full rounded-full bg-brand-500 transition-[width]"
            style={{ width: `${barWidth}%` }}
          />
        </div>
      )}
    </div>
  );
}
